/**
 * Türkçe Zod error messages helper
 *
 * Zod default error mesajları İngilizce. SWSkills'in tüm kullanıcı-facing
 * hataları Türkçe (CLAUDE.md hard rule).
 *
 * Kullanım:
 *   import { setupTurkishZodErrors } from '@swskills/shared';
 *   setupTurkishZodErrors();
 */
import { z, type ZodErrorMap } from 'zod';

export const turkishErrorMap: ZodErrorMap = (issue, ctx) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === 'undefined') return { message: 'Bu alan zorunludur' };
      return { message: `Geçersiz tip. Beklenen: ${issue.expected}, alınan: ${issue.received}` };

    case z.ZodIssueCode.invalid_string:
      if (issue.validation === 'email') return { message: 'Geçerli bir e-posta adresi girin' };
      if (issue.validation === 'url') return { message: 'Geçerli bir URL girin' };
      if (issue.validation === 'uuid') return { message: 'Geçersiz UUID' };
      if (issue.validation === 'datetime') return { message: 'Geçerli bir tarih girin' };
      return { message: `Geçersiz değer: ${ctx.defaultError}` };

    case z.ZodIssueCode.too_small:
      if (issue.type === 'string') {
        return { message: `En az ${issue.minimum} karakter olmalı` };
      }
      if (issue.type === 'number') {
        return { message: `En az ${issue.minimum} olmalı` };
      }
      if (issue.type === 'array') {
        return { message: `En az ${issue.minimum} öğe seçin` };
      }
      return { message: `Çok küçük` };

    case z.ZodIssueCode.too_big:
      if (issue.type === 'string') {
        return { message: `En fazla ${issue.maximum} karakter olabilir` };
      }
      if (issue.type === 'number') {
        return { message: `En fazla ${issue.maximum} olabilir` };
      }
      if (issue.type === 'array') {
        return { message: `En fazla ${issue.maximum} öğe seçilebilir` };
      }
      return { message: `Çok büyük` };

    case z.ZodIssueCode.invalid_enum_value:
      return {
        message: `Geçersiz seçim. Olası değerler: ${issue.options.join(', ')}`,
      };

    case z.ZodIssueCode.unrecognized_keys:
      return { message: `Tanınmayan alanlar: ${issue.keys.join(', ')}` };

    case z.ZodIssueCode.custom:
      return { message: (issue.params?.['message'] as string) ?? 'Geçersiz değer' };

    default:
      return { message: ctx.defaultError };
  }
};

/**
 * Global Zod error map'i Türkçe yap.
 * apps/api/src/index.ts ve apps/web-{public,internal}/src/main.tsx'in başında çağrılır.
 */
export function setupTurkishZodErrors(): void {
  z.setErrorMap(turkishErrorMap);
}

/**
 * Zod hatasını UI-friendly Türkçe stringe çevir.
 *
 *   try { schema.parse(input) }
 *   catch (e) { return formatZodErrors(e) }
 */
export function formatZodErrors(error: z.ZodError): Record<string, string> {
  const result: Record<string, string> = {};
  for (const issue of error.issues) {
    const path = issue.path.join('.');
    if (!result[path]) result[path] = issue.message;
  }
  return result;
}
